import type { CameraOptions } from '@mindcloudxai/shared';
import type { NavigationMode, NavigationModeContext, NavigationModeKey } from './types';
import { clamp, length } from './shared';

export class OrbitMode implements NavigationMode {
  readonly key: NavigationModeKey = 'orbit';

  private ctx: NavigationModeContext | null = null;
  private azimuth = 0;
  private elevation = 0.3;
  private radius = 4;
  private targetX = 0;
  private targetY = 0;
  private targetZ = 0;
  private isDragging = false;
  private isRightDrag = false;
  private lastX = 0;
  private lastY = 0;

  activate(ctx: NavigationModeContext) {
    this.ctx = ctx;
    const el = ctx.canvas;
    this.syncFromCamera(ctx.renderer.getCamera());

    el.addEventListener('pointerdown', this.onPointerDown);
    el.addEventListener('pointermove', this.onPointerMove);
    el.addEventListener('pointerup', this.onPointerUp);
    el.addEventListener('pointercancel', this.onPointerUp);
    el.addEventListener('wheel', this.onWheel, { passive: true });
    el.addEventListener('contextmenu', this.onContextMenu);

    this.apply();
  }

  deactivate() {
    if (!this.ctx) return;
    const el = this.ctx.canvas;
    el.removeEventListener('pointerdown', this.onPointerDown);
    el.removeEventListener('pointermove', this.onPointerMove);
    el.removeEventListener('pointerup', this.onPointerUp);
    el.removeEventListener('pointercancel', this.onPointerUp);
    el.removeEventListener('wheel', this.onWheel);
    el.removeEventListener('contextmenu', this.onContextMenu);
    this.ctx = null;
  }

  private onPointerDown = (e: PointerEvent) => {
    this.isDragging = true;
    this.isRightDrag = e.button === 2;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  private onPointerMove = (e: PointerEvent) => {
    if (!this.isDragging) return;
    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;

    if (this.isRightDrag) {
      const panScale = this.radius * 0.001;
      this.targetX -= dx * panScale;
      this.targetY += dy * panScale;
    } else {
      this.azimuth -= dx * 0.005;
      this.elevation = Math.max(
        -Math.PI / 2 + 0.05,
        Math.min(Math.PI / 2 - 0.05, this.elevation + dy * 0.005),
      );
    }
    this.apply();
  };

  private onPointerUp = (e: PointerEvent) => {
    this.isDragging = false;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  };

  private onWheel = (e: WheelEvent) => {
    this.radius = Math.max(0.5, this.radius + e.deltaY * 0.01);
    this.apply();
  };

  private onContextMenu = (e: Event) => e.preventDefault();

  private syncFromCamera(camera: CameraOptions) {
    this.targetX = camera.target.x;
    this.targetY = camera.target.y;
    this.targetZ = camera.target.z;
    const dx = camera.position.x - camera.target.x;
    const dy = camera.position.y - camera.target.y;
    const dz = camera.position.z - camera.target.z;
    this.radius = Math.max(length({ x: dx, y: dy, z: dz }), 0.5);
    this.elevation = clamp(Math.asin(dy / this.radius), -Math.PI / 2 + 0.05, Math.PI / 2 - 0.05);
    this.azimuth = Math.atan2(dx, dz);
  }

  private apply() {
    if (!this.ctx) return;
    const x = this.targetX + this.radius * Math.cos(this.elevation) * Math.sin(this.azimuth);
    const y = this.targetY + this.radius * Math.sin(this.elevation);
    const z = this.targetZ + this.radius * Math.cos(this.elevation) * Math.cos(this.azimuth);
    this.ctx.renderer.setCamera({
      position: { x, y, z },
      target: { x: this.targetX, y: this.targetY, z: this.targetZ },
    });
  }
}
